import loadable from '@loadable/component';

import { Box } from '@simplywallst/ui-core';

import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';

import { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { usePortfolioPerformanceTimeseries } from '@/hooks/portfolio/usePortfolioPerformanceTimeseries';

import { Chart } from './components/Chart';
import { ResponsiveWidth } from './ResponsiveWidth';

const EmptyState = loadable(() => import('./components/EmptyState'), {
  resolveComponent: (c) => c.EmptyState,
});

const ErrorState = loadable(() => import('./components/ErrorState'), {
  resolveComponent: (c) => c.ErrorState,
});

const LoadingState = loadable(() => import('./components/LoadingState'), {
  resolveComponent: (c) => c.LoadingState,
});

const getTimePeriod = () => {
  const dateISOString = new Date().toISOString();
  const today = dateISOString.slice(0, dateISOString.indexOf('T'));

  const periodMap = getTimeSeriesPeriodMap(new Date(today));

  return {
    startTimestamp: periodMap['1Y'],
    endTimestamp: periodMap.NOW,
  };
};

const chartHeight = 55;

type Props = {
  portfolioId: Portfolio['id'];
};

export const MiniPortfolioPerformanceChart = ({ portfolioId }: Props) => {
  const { startTimestamp, endTimestamp } = getTimePeriod();
  const { data, status } = usePortfolioPerformanceTimeseries({
    portfolioId,
    startTimestamp,
    endTimestamp,
  });

  const isEmpty = status === 'success' && data?.length === 0;

  if (status === 'pending') return <LoadingState />;
  if (status === 'error') return <ErrorState />;
  if (isEmpty || typeof data === 'undefined') return <EmptyState />;

  return (
    <ResponsiveWidth>
      {({ width }) => (
        <Box height={chartHeight}>
          <Chart timeseries={data} width={width} height={chartHeight} />
        </Box>
      )}
    </ResponsiveWidth>
  );
};
